import { useMemo } from 'react';
import { Box, IconButton } from '@mui/material';
import Picker, { IEmojiData } from 'emoji-picker-react';
import _uniq from 'lodash/uniq';
import { TReaction } from '@/types/api';
import { getComparator } from '@/utils/order';
import SmileEmoji from './Reactions.icon';
import styles from './Reactions.styles';

type ReactionsType = {
  currentUserId: string;
  reactions?: TReaction[];
  isShowingEmojiPicker: boolean;
  toggleEmojiPickerVisibility: () => void;
  onRemoveReaction: (id: string) => void;
  onAddNewReaction: (emoji: string) => void;
};

const Reactions = function ({
  reactions,
  currentUserId,
  toggleEmojiPickerVisibility,
  isShowingEmojiPicker,
  onRemoveReaction,
  onAddNewReaction,
}: ReactionsType): JSX.Element {
  const reactionsFallback = useMemo(() => reactions ?? [], [reactions]);

  const reactionsFormatted = useMemo(() => {
    const reactionsByDate = reactionsFallback.sort(getComparator('asc', 'createdAt'));
    const groupedByEmoji = _uniq(reactionsByDate.map(({ emoji }) => emoji));

    return groupedByEmoji.map((emoji) => {
      const filteredByEmoji = reactionsFallback.filter((reaction) => reaction.emoji === emoji);

      return {
        emoji,
        hasReactedWithEmoji: filteredByEmoji.some((reaction) => reaction.userId === currentUserId),
        counter: filteredByEmoji.length,
      };
    });
  }, [currentUserId, reactionsFallback]);

  const userReactions = useMemo(() => {
    return reactionsFallback.filter((reaction) => reaction.userId === currentUserId) ?? [];
  }, [currentUserId, reactionsFallback]);

  const onEmojiClick = (
    _evt: React.MouseEvent<Element, MouseEvent>,
    selectedEmoji: IEmojiData
  ): void => {
    const hasSelectedEmoji = userReactions.find(
      (reaction) => reaction.emoji === selectedEmoji.emoji
    );
    toggleEmojiPickerVisibility();
    if (hasSelectedEmoji?.id) {
      onRemoveReaction(hasSelectedEmoji?.id);
      return;
    }
    onAddNewReaction(selectedEmoji.emoji);
  };

  return (
    <Box sx={styles.wrapper} data-testid="reactions-id">
      <IconButton
        data-testid="open-emoji-picker"
        color="primary"
        aria-label={`${isShowingEmojiPicker ? 'open emoji picker' : 'close emoji picker'}`}
        onClick={toggleEmojiPickerVisibility}
      >
        <SmileEmoji />
      </IconButton>
      {isShowingEmojiPicker && (
        <Box data-testid="emoji-picker" sx={styles.picker}>
          <Picker onEmojiClick={onEmojiClick} />
        </Box>
      )}
      <Box>
        {reactionsFormatted.map(({ emoji, counter, hasReactedWithEmoji }) => {
          const emojiReacted = (userReactions ?? []).find((reaction) => reaction.emoji === emoji);

          return (
            <IconButton
              data-testid={`emoji-${emoji}`}
              onClick={() =>
                hasReactedWithEmoji
                  ? onRemoveReaction(emojiReacted?.id ?? '')
                  : onAddNewReaction(emoji)
              }
              key={emoji}
              sx={styles.emojiButton}
              color="success"
              size="small"
            >
              {emoji}
              <Box
                data-testid={`emoji-${emoji}-counter-${counter}`}
                sx={styles.emojiCounter}
                component="span"
              >
                {counter}
              </Box>
            </IconButton>
          );
        })}
      </Box>
    </Box>
  );
};

export default Reactions;
